import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import AdminLayout from "@/components/AdminLayout";
import { crmApi } from "@/utils/api";

const CRMDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await crmApi.getStats();
      setStats(response.data);
    } catch (error) {
      // Error handled silently in production
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="crm-dashboard">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white">Dashboard CRM</h1>
            <p className="text-[#A3A3A3] text-sm">Gestiona tus contactos</p>
          </div>
          <Link to="/admin/crm/contacts/new">
            <Button className="bg-[#D4AF37] text-black hover:bg-[#FFE082]">
              <UserPlus size={18} className="mr-2" />
              Nuevo Contacto
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#121212] border-white/5">
            <CardContent className="p-6">
              <p className="text-[#A3A3A3] text-sm">Total Contactos</p>
              <p className="text-3xl font-bold text-white">{stats?.total_contacts || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-white/5">
            <CardContent className="p-6">
              <p className="text-[#A3A3A3] text-sm">Vendedores</p>
              <p className="text-3xl font-bold text-white">{stats?.sellers || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-white/5">
            <CardContent className="p-6">
              <p className="text-[#A3A3A3] text-sm">Compradores</p>
              <p className="text-3xl font-bold text-white">{stats?.buyers || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-white/5">
            <CardContent className="p-6">
              <p className="text-[#A3A3A3] text-sm">Corredores</p>
              <p className="text-3xl font-bold text-white">{stats?.brokers || 0}</p>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-[#121212] border-white/5">
          <CardHeader>
            <CardTitle className="text-white">Pipeline por Etapa</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {stats?.by_stage && Object.entries(stats.by_stage).map(([id, stage]) => (
                <Link key={id} to={`/admin/crm/contacts?stage=${id}`} className="text-center p-4 rounded bg-[#1a1a1a]">
                  <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ backgroundColor: stage.color }}></div>
                  <p className="text-xl font-bold text-white">{stage.count}</p>
                  <p className="text-xs text-[#A3A3A3]">{stage.name}</p>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Link to="/admin/crm/contacts" className="text-[#D4AF37] hover:underline">Ver Contactos</Link>
          <Link to="/admin/crm/team" className="text-[#D4AF37] hover:underline">Equipo</Link>
          <Link to="/admin/crm/stages" className="text-[#D4AF37] hover:underline">Etapas</Link>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CRMDashboard;
